import {
  Controller,
  Control,
  UseFormSetValue,
  FieldErrors,
} from 'react-hook-form';
import { round } from 'lodash';
import { Box, Grid, useTheme } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { useState } from 'react';
import FieldRow from 'components/FormElements/FieldRow';
import AppRadioButton from 'components/FormElements/AppRadioButton';
import AppTextInput from 'components/FormElements/AppTextInput';
import FormLabel from 'components/FormElements/FormLabel';
import GridContainer from 'components/GridContainer';
import SharePriceInfoTooltip from './SharePriceInfoTooltip';
import TaxReliefElegibleAllocations from './TaxReliefElegibleAllocations';
import Row from 'components/Layout/Row';
import TaxReliefButton from './components/TaxReliefButton';
import { TaxReliefElegibleType, ConsiderationType } from 'further-types/exit';
import { styled } from '@mui/material';
import type { ExitDetailsFormValues } from './index';
import { PreviousExitDetails } from './types';

const StyledFieldRow = styled(FieldRow)(() => ({
  minHeight: '42px',
}));

const CashConsiderationLabel = styled(Box)(() => ({
  whiteSpace: 'nowrap',
  minWidth: '80px',
  display: 'flex',
  alignItems: 'center',
  paddingRight: '0rem',
}));

const CashConsiderationTotalLabel = styled(Box)(() => ({
  whiteSpace: 'nowrap',
  minWidth: '50px',
  display: 'flex',
  alignItems: 'center',
}));

const CashConsiderationInputWrapper = styled(Box)(() => ({
  flex: 1,
}));

const StyledErrorText = styled('p')(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: theme.fontsizes?.sm,
}));

const PaddedAppTextInput = styled(AppTextInput)(() => ({
  paddingRight: '2em',
}));

function moveSelectionToEndOnFocus(
  e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
) {
  const target = e.target;
  if (!(target instanceof HTMLInputElement) || !target.value) return;
  queueMicrotask(() => {
    if (document.activeElement === target) {
      const selectionStart = target.selectionStart ?? 0;
      const selectionEnd = target.selectionEnd ?? 0;
      if (selectionStart === 0 && selectionEnd === target.value.length) {
        const len = target.value.length;
        target.setSelectionRange(len, len);
      }
    }
  });
}
type CashConsiderationFieldsProps = {
  control: Control<ExitDetailsFormValues>;
  setValue: UseFormSetValue<ExitDetailsFormValues>;
  errors: FieldErrors<ExitDetailsFormValues>;
  isEditMode: boolean;
  disableEdit: boolean;
  considerationType: ConsiderationType | undefined;
  totalConsiderationShares: string;
  taxReliefElegible?: TaxReliefElegibleType;
  isReceivedValueSharePriceEdited: boolean;
  isBeingCorrected?: boolean;
  isCorrectingAmendment?: boolean;
  firmId: string | undefined;
  selectedShareClasses: Array<string>;
  selectedAllocations: Array<string>;
  taxReliefElegibleAllocationIds: Array<string>;
  previousExitDetails?: PreviousExitDetails;
  sharesToBeSold: number;
  lastManuallyEditedFieldTaxable: React.MutableRefObject<
    'perShare' | 'total' | null
  >;
  lastManuallyEditedFieldReceived: React.MutableRefObject<
    'perShare' | 'total' | null
  >;
  calculateCashConsiderationValuesTaxable: (
    changedField: 'perShare' | 'total',
    newValue?: string,
  ) => void;
  calculateCashConsiderationValuesReceived: (
    changedField: 'perShare' | 'total',
    newValue?: string,
  ) => void;
  hasTaxableValueIncrease: boolean;
  originalTaxReliefElegible?: TaxReliefElegibleType;
};

const CashConsiderationFields: React.FC<CashConsiderationFieldsProps> = ({
  control,
  setValue,
  errors,
  isEditMode,
  disableEdit,
  considerationType,
  totalConsiderationShares,
  taxReliefElegible,
  isReceivedValueSharePriceEdited,
  isBeingCorrected,
  isCorrectingAmendment,
  firmId,
  selectedShareClasses,
  selectedAllocations,
  taxReliefElegibleAllocationIds,
  previousExitDetails,
  lastManuallyEditedFieldTaxable,
  lastManuallyEditedFieldReceived,
  calculateCashConsiderationValuesTaxable,
  calculateCashConsiderationValuesReceived,
  hasTaxableValueIncrease,
  originalTaxReliefElegible,
}) => {
  const theme = useTheme();
  const [focusedField, setFocusedField] = useState<
    | 'taxablePerShare'
    | 'taxableTotal'
    | 'receivedPerShare'
    | 'receivedTotal'
    | null
  >(null);

  const getDisplayValue = (
    value: string | number | undefined,
    fieldName:
      | 'taxablePerShare'
      | 'taxableTotal'
      | 'receivedPerShare'
      | 'receivedTotal',
  ): string => {
    // Only show empty when focused and value is exactly '0' (not when editing non-zero values)
    const stringValue = value?.toString() || '';
    if (focusedField === fieldName && stringValue === '0') {
      return '';
    }
    return stringValue;
  };

  // When correcting the original exit (not an amendment), no restrictions should apply
  const isCorrectingOriginalExit = isBeingCorrected && !isCorrectingAmendment;

  // Compute greying-out conditions for S/EIS eligible exit field
  // Restrictions only apply when amending or editing, not when correcting the original
  const isEntireFieldDisabled =
    isEditMode && !hasTaxableValueIncrease && !isCorrectingOriginalExit;

  // Disable Yes when original was No or Partial
  // But this does NOT apply when correcting the original exit
  const isYesDisabled =
    !isCorrectingOriginalExit &&
    isEditMode &&
    hasTaxableValueIncrease &&
    (originalTaxReliefElegible === TaxReliefElegibleType.No ||
      originalTaxReliefElegible === TaxReliefElegibleType.Partial);

  // Disable No only when original was No (not Partial)
  const isNoDisabled =
    !isCorrectingOriginalExit &&
    isEditMode &&
    hasTaxableValueIncrease &&
    originalTaxReliefElegible === TaxReliefElegibleType.No;

  // Disable Partial only when original was No
  const isPartialDisabled =
    !isCorrectingOriginalExit &&
    isEditMode &&
    hasTaxableValueIncrease &&
    originalTaxReliefElegible === TaxReliefElegibleType.No;

  if (considerationType !== ConsiderationType.Cash) {
    return null;
  }

  return (
    <>
      <StyledFieldRow
        title="Taxable cash consideration"
        centerTitle
        tooltipText="This is typically the total consideration or sale share price agreed in any legal sale documentation. Disregard share consideration value, if applicable."
      >
        <Row spacing="sm" fullWidth>
          <CashConsiderationLabel>
            <FormLabel
              title="Per share"
              labelColor={theme.palette.text.secondary}
            />
          </CashConsiderationLabel>
          <CashConsiderationInputWrapper>
            <Controller
              name="taxableValueSharePrice"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumericFormat
                  onValueChange={({ value }, sourceInfo) => {
                    const numValue = Number(value);
                    const normalizedValue = isNaN(numValue)
                      ? value
                      : round(numValue, 12).toString();
                    field.onChange(normalizedValue);
                    if (sourceInfo.source !== 'event') return;
                    lastManuallyEditedFieldTaxable.current = 'perShare';
                    calculateCashConsiderationValuesTaxable(
                      'perShare',
                      normalizedValue,
                    );
                    // Only auto-sync received value during creation, not during correction
                    if (!isReceivedValueSharePriceEdited && !isBeingCorrected) {
                      setValue('sharePrice', normalizedValue);
                      calculateCashConsiderationValuesReceived(
                        'perShare',
                        normalizedValue,
                      );
                    }
                  }}
                  value={getDisplayValue(field.value, 'taxablePerShare')}
                  onFocus={(e) => {
                    setFocusedField('taxablePerShare');
                    if (field.value === '0') {
                      field.onChange('');
                    }
                    moveSelectionToEndOnFocus(e);
                  }}
                  onBlur={(_e) => {
                    setFocusedField(null);
                    if (!field.value || field.value === '') {
                      field.onChange('0');
                    }
                    field.onBlur();
                  }}
                  prefix="£"
                  decimalScale={12}
                  allowNegative={false}
                  allowLeadingZeros={false}
                  thousandSeparator={true}
                  customInput={PaddedAppTextInput}
                  error={!!error}
                  helperText={error?.message}
                  disabled={disableEdit}
                  size="small"
                  placeholder="0"
                />
              )}
            />
          </CashConsiderationInputWrapper>
          <CashConsiderationTotalLabel>
            <FormLabel
              title="Total"
              labelColor={theme.palette.text.secondary}
            />
          </CashConsiderationTotalLabel>
          <CashConsiderationInputWrapper>
            <Controller
              name="taxableValueTotal"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumericFormat
                  onValueChange={({ value }, sourceInfo) => {
                    field.onChange(value);
                    if (sourceInfo.source !== 'event') return;
                    lastManuallyEditedFieldTaxable.current = 'total';
                    calculateCashConsiderationValuesTaxable('total', value);
                    // Only auto-sync received value during creation, not during correction
                    if (!isReceivedValueSharePriceEdited && !isBeingCorrected) {
                      setValue('receivedValueTotal', value);
                      calculateCashConsiderationValuesReceived('total', value);
                    }
                  }}
                  value={getDisplayValue(field.value, 'taxableTotal')}
                  onFocus={() => {
                    setFocusedField('taxableTotal');
                    if (field.value === '0') {
                      field.onChange('');
                    }
                  }}
                  onBlur={(_e) => {
                    setFocusedField(null);
                    if (!field.value || field.value === '') {
                      field.onChange('0');
                    }
                    field.onBlur();
                  }}
                  prefix="£"
                  decimalScale={2}
                  allowNegative={false}
                  allowLeadingZeros={false}
                  thousandSeparator={true}
                  customInput={AppTextInput}
                  error={!!error}
                  helperText={error?.message}
                  disabled={disableEdit}
                  size="small"
                  placeholder="0"
                />
              )}
            />
          </CashConsiderationInputWrapper>
        </Row>
      </StyledFieldRow>
      <StyledFieldRow
        title="Received cash consideration"
        centerTitle
        tooltipText={
          <span>
            This must be less than or equal to the{' '}
            <em>taxable consideration</em>, depending on whether all sale
            proceeds have been received or not. Disregard share consideration
            value, if applicable.
          </span>
        }
      >
        <Row spacing="sm" fullWidth>
          <CashConsiderationLabel>
            <FormLabel
              title="Per share"
              labelColor={theme.palette.text.secondary}
            />
          </CashConsiderationLabel>
          <CashConsiderationInputWrapper>
            <Controller
              name="sharePrice"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumericFormat
                  onValueChange={({ value }, sourceInfo) => {
                    const numValue = Number(value);
                    const normalizedValue = isNaN(numValue)
                      ? value
                      : round(numValue, 12).toString();
                    field.onChange(normalizedValue);
                    if (sourceInfo.source !== 'event') return;
                    lastManuallyEditedFieldReceived.current = 'perShare';
                    calculateCashConsiderationValuesReceived(
                      'perShare',
                      normalizedValue,
                    );
                    setValue('isReceivedValueSharePriceEdited', true);
                  }}
                  value={getDisplayValue(field.value, 'receivedPerShare')}
                  onFocus={(e) => {
                    setFocusedField('receivedPerShare');
                    if (field.value === '0') {
                      field.onChange('');
                    }
                    moveSelectionToEndOnFocus(e);
                  }}
                  onBlur={(_e) => {
                    setFocusedField(null);
                    if (!field.value || field.value === '') {
                      field.onChange('0');
                    }
                    field.onBlur();
                  }}
                  prefix="£"
                  decimalScale={12}
                  allowNegative={false}
                  allowLeadingZeros={false}
                  thousandSeparator={true}
                  customInput={PaddedAppTextInput}
                  error={!!error}
                  helperText={error?.message}
                  disabled={disableEdit}
                  size="small"
                  placeholder="0"
                />
              )}
            />
          </CashConsiderationInputWrapper>
          <CashConsiderationTotalLabel>
            <FormLabel
              title="Total"
              labelColor={theme.palette.text.secondary}
            />
          </CashConsiderationTotalLabel>
          <CashConsiderationInputWrapper>
            <Controller
              name="receivedValueTotal"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumericFormat
                  onValueChange={({ value }, sourceInfo) => {
                    field.onChange(value);
                    if (sourceInfo.source !== 'event') return;
                    lastManuallyEditedFieldReceived.current = 'total';
                    calculateCashConsiderationValuesReceived('total', value);
                    setValue('isReceivedValueSharePriceEdited', true);
                  }}
                  value={getDisplayValue(field.value, 'receivedTotal')}
                  onFocus={() => {
                    setFocusedField('receivedTotal');
                    if (field.value === '0') {
                      field.onChange('');
                    }
                  }}
                  onBlur={(_e) => {
                    setFocusedField(null);
                    if (!field.value || field.value === '') {
                      field.onChange('0');
                    }
                    field.onBlur();
                  }}
                  prefix="£"
                  decimalScale={2}
                  allowNegative={false}
                  allowLeadingZeros={false}
                  thousandSeparator={true}
                  customInput={AppTextInput}
                  error={!!error}
                  helperText={error?.message}
                  disabled={disableEdit}
                  size="small"
                  placeholder="0"
                />
              )}
            />
          </CashConsiderationInputWrapper>
        </Row>
      </StyledFieldRow>
      <StyledFieldRow
        title="Charge accrued fees"
        centerTitle
        tooltipText="Deduct any fees that have been accrued to date but not paid by the investor. The maximum fee chargeable to any investor on exit is their full exit amount. Any accrued fees not charged now will remain on the system for charging at a later date."
      >
        <Controller
          name="chargeAccruedFees"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <AppRadioButton
                name="chargeAccruedFees"
                label="Yes"
                value={1}
                checked={field.value === true}
                onChange={(_, checked) => {
                  if (checked) {
                    field.onChange(true);
                  }
                }}
                error={!!error}
                disabled={disableEdit}
              />
              <AppRadioButton
                name="chargeAccruedFees"
                label="No"
                value={0}
                checked={field.value === false}
                onChange={(_, checked) => {
                  if (checked) {
                    field.onChange(false);
                  }
                }}
                error={!!error}
                disabled={disableEdit}
              />
              {error && <StyledErrorText>{error.message}</StyledErrorText>}
            </>
          )}
        />
      </StyledFieldRow>
      {(considerationType === ConsiderationType.Cash ||
        (considerationType === ConsiderationType.Share &&
          totalConsiderationShares &&
          Number(totalConsiderationShares) > 0)) && (
        <StyledFieldRow
          title="S/EIS eligible exit"
          centerTitle
          tooltipText={
            <span>
              {isEntireFieldDisabled && (
                <>
                  <span>
                    This exit's S/EIS eligible status has already been set. If
                    there is no taxable value increase, an edit must follow the
                    original exit's tax status.
                  </span>
                  <br />
                  <br />
                </>
              )}
              <span>
                Editing the S/EIS status of this exit will change the status
                from the date of this update, not the original exit date. If you
                have received earnout consideration which increases the taxable
                value share price, this consideration is not typically S/EIS
                eligible and this update should be marked 'No' unless you have
                received tax advice otherwise. Receipt of escrow release
                proceeds is typically S/EIS eligible.
              </span>
            </span>
          }
        >
          <Controller
            name="taxReliefElegible"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <TaxReliefButton
                  option={TaxReliefElegibleType.Yes}
                  checked={field.value === TaxReliefElegibleType.Yes}
                  onChange={field.onChange}
                  error={!!error}
                  disableEdit={disableEdit}
                  isEntireFieldDisabled={isEntireFieldDisabled}
                  isYesDisabled={isYesDisabled}
                  isNoDisabled={isNoDisabled}
                  isPartialDisabled={isPartialDisabled}
                  originalTaxReliefElegible={originalTaxReliefElegible}
                />
                <TaxReliefButton
                  option={TaxReliefElegibleType.No}
                  checked={field.value === TaxReliefElegibleType.No}
                  onChange={field.onChange}
                  error={!!error}
                  disableEdit={disableEdit}
                  isEntireFieldDisabled={isEntireFieldDisabled}
                  isYesDisabled={isYesDisabled}
                  isNoDisabled={isNoDisabled}
                  isPartialDisabled={isPartialDisabled}
                  originalTaxReliefElegible={originalTaxReliefElegible}
                />
                <TaxReliefButton
                  option={TaxReliefElegibleType.Partial}
                  checked={field.value === TaxReliefElegibleType.Partial}
                  onChange={field.onChange}
                  error={!!error}
                  disableEdit={disableEdit}
                  isEntireFieldDisabled={isEntireFieldDisabled}
                  isYesDisabled={isYesDisabled}
                  isNoDisabled={isNoDisabled}
                  isPartialDisabled={isPartialDisabled}
                  originalTaxReliefElegible={originalTaxReliefElegible}
                />
                {error && <StyledErrorText>{error.message}</StyledErrorText>}
              </>
            )}
          />
        </StyledFieldRow>
      )}
      {taxReliefElegible === TaxReliefElegibleType.Partial &&
        considerationType === ConsiderationType.Cash &&
        (isEditMode
          ? Array.isArray(previousExitDetails?.selectedShareClasses) &&
            previousExitDetails.selectedShareClasses.length > 0 &&
            Array.isArray(previousExitDetails?.shareAllocationUploadIds) &&
            previousExitDetails.shareAllocationUploadIds.length > 0
          : firmId &&
            selectedShareClasses.length > 0 &&
            selectedAllocations.length > 0) && (
          <TaxReliefElegibleAllocations
            firmId={firmId ?? ''}
            selectedShareClasses={
              isEditMode
                ? previousExitDetails?.selectedShareClasses ?? []
                : selectedShareClasses
            }
            selectedAllocations={
              isEditMode
                ? previousExitDetails?.shareAllocationUploadIds ?? []
                : selectedAllocations
            }
            taxReliefElegibleAllocationIds={taxReliefElegibleAllocationIds}
            onChange={(value) =>
              setValue('taxReliefElegibleAllocationIds', value)
            }
            disabled={disableEdit}
            error={errors.taxReliefElegibleAllocationIds?.message}
          />
        )}
      {considerationType === ConsiderationType.Cash && (
        <GridContainer>
          <Grid size={{ md: 10, xs: 12 }}>
            <SharePriceInfoTooltip />
          </Grid>
        </GridContainer>
      )}
    </>
  );
};

export default CashConsiderationFields;
